import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowRight, AlertCircle, Lock, Sparkles, Cpu, UserPlus, KeyRound, ScanLine } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { HologramPortrait } from "@/components/fx/HologramPortrait";
import { ShowreelLauncher } from "@/components/showreel/ShowreelLauncher";
import { SentinelStage } from "@/components/fx/SentinelStage";

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success("Authenticated", { description: "Welcome to UTSAVA Genesis" });
            nav("/");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Authentication failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full relative" data-testid="login-page">
            {/* ---- cinematic VFX stage (z0-z5, fixed, behind everything) ---- */}
            <SentinelStage />

            {/* ---- content (z10) ---- */}
            <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_540px]">
                {/* Left — brand / narrative column, inset so the shield artwork stays visible */}
                <div className="hidden lg:flex flex-col justify-between p-12 xl:p-14 lg:pl-[26vw] xl:pl-[24vw]">
                    <motion.div {...fadeUp(0.05)} className="flex items-center gap-3">
                        <div className="relative">
                            <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-rose-400 via-rose-600 to-[#7A0A14] flex items-center justify-center shadow-[0_0_38px_rgb(var(--accent-1-rgb) / 0.5)] chrome-bevel">
                                <Shield size={22} className="text-white" strokeWidth={2.5} />
                            </div>
                            <span className="absolute -bottom-1 -right-1 radar-dot" />
                        </div>
                        <div>
                            <div className="font-display text-2xl tracking-wide text-white drop-shadow-[0_0_18px_rgb(var(--accent-1-rgb) / 0.35)]">
                                UTSAVA <span className="text-cyan-300">Genesis</span>
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400/70">
                                um-alphabydesign · Powered by ASSEP
                            </div>
                        </div>
                    </motion.div>

                    <div className="max-w-xl">
                        <motion.div {...fadeUp(0.25)}>
                            <div className="label-eyebrow mb-4 flex items-center gap-2">
                                <ScanLine size={11} className="text-cyan-400" />
                                Autonomous SAP Security Engineering Platform
                            </div>
                            <h2 className="font-display font-light text-5xl xl:text-6xl leading-[1.02] text-white tracking-tight">
                                The future of <span className="text-cyan-300 drop-shadow-[0_0_30px_rgb(var(--accent-1-rgb) / 0.55)]">SAP&nbsp;Security</span>
                                <br />
                                Engineering—<span className="text-amber-400 italic font-extralight">offline</span>.
                            </h2>
                            <p className="mt-7 text-slate-300/80 text-sm leading-relaxed max-w-md">
                                Unified Trusted Security Authorization &amp; Validation Architecture.
                                Autonomous inspection of SAP engineering artifacts with explainable
                                recommendations — no live system connectivity required.
                            </p>
                        </motion.div>

                        <motion.div {...fadeUp(0.45)} className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                            {[
                                { k: "Engines", v: "04", Icon: Cpu },
                                { k: "SoD Risks", v: "258", Icon: Shield },
                                { k: "Reasoning", v: "Agentic", Icon: Sparkles },
                            ].map((s) => (
                                <div key={s.k} className="panel px-4 py-3 backdrop-blur-md bg-black/45 hover:border-cyan-500/40 transition-colors">
                                    <div className="flex items-center justify-between mb-1">
                                        <s.Icon size={11} className="text-cyan-400/80" />
                                    </div>
                                    <div className="font-display text-cyan-300 text-2xl drop-shadow-[0_0_12px_rgb(var(--accent-1-rgb) / 0.4)]">{s.v}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-1">{s.k}</div>
                                </div>
                            ))}
                        </motion.div>

                        <motion.div {...fadeUp(0.6)} className="mt-6">
                            <ShowreelLauncher />
                        </motion.div>
                    </div>

                    <motion.div {...fadeUp(0.75)} className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
                        <span>© 2026 · UTSAVA Genesis</span>
                        <span className="flex items-center gap-2">
                            <span className="dot" style={{ background: "var(--neon-cyan)", boxShadow: "0 0 10px var(--neon-cyan)" }} />
                            All systems nominal
                        </span>
                    </motion.div>
                </div>

                {/* Right — machined-steel auth console */}
                <div className="flex items-center justify-center p-6 sm:p-8 lg:p-10">
                    <motion.div
                        initial={{ opacity: 0, y: 28, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.75, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-md sentinel-auth-frame rounded-[6px] p-7 sm:p-9"
                    >
                        <div className="lg:hidden flex items-center gap-3 mb-7">
                            <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-rose-400 to-rose-700 flex items-center justify-center">
                                <Shield size={20} className="text-white" strokeWidth={2.5} />
                            </div>
                            <div className="font-display text-xl text-white">UTSAVA <span className="text-cyan-400">Genesis</span></div>
                        </div>

                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="label-eyebrow mb-2">Secure Sign-in · Architect Console</div>
                                <h1 className="font-display text-3xl text-white tracking-tight">Authenticate to Genesis</h1>
                                <p className="text-sm text-slate-500 mt-2">
                                    httpOnly cookie session · workspace isolation · audit-trailed
                                </p>
                            </div>
                            <HologramPortrait
                                testId={TID.loginHologram}
                                className="hidden sm:block w-[72px] h-[72px] shrink-0"
                                showLabel={false}
                            />
                        </div>

                        <form onSubmit={submit} className="mt-8 space-y-5" autoComplete="off">
                            <div>
                                <label className="label-eyebrow block mb-2">Email</label>
                                <input
                                    data-testid={TID.loginEmail}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="field"
                                    placeholder="you@company.com"
                                    required
                                    autoFocus
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-2">Password</label>
                                <div className="relative">
                                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                    <input
                                        data-testid={TID.loginPassword}
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="field pl-9"
                                        placeholder="••••••••••"
                                        required
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    data-testid={TID.loginError}
                                    className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs rounded-sm"
                                >
                                    <AlertCircle size={14} className="mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                data-testid={TID.loginSubmit}
                                type="submit"
                                disabled={loading}
                                className="btn-primary sheen w-full flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {loading ? "Authenticating…" : "Initiate Session"}
                                {!loading && <ArrowRight size={14} />}
                            </button>
                        </form>

                        <div className="mt-6 flex items-center justify-between text-xs">
                            <Link
                                to="/forgot-password"
                                data-testid="forgot-password-link"
                                className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1.5 font-mono uppercase tracking-wider text-[11px]"
                            >
                                <KeyRound size={12} /> Forgot password
                            </Link>
                            <Link
                                to="/signup"
                                data-testid="signup-link"
                                className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1.5 font-mono uppercase tracking-wider text-[11px]"
                            >
                                <UserPlus size={12} /> Request access
                            </Link>
                        </div>

                        <div className="mt-7 p-4 border border-cyan-500/15 rounded-sm bg-cyan-500/[0.03]">
                            <div className="label-eyebrow mb-2">Access Policy</div>
                            <p className="font-mono text-[11px] text-slate-400 leading-relaxed">
                                Self-service registration available. Choose one or more execution
                                roles. Admin access is reserved for platform owners only.
                            </p>
                        </div>

                        <div className="mt-7 lg:hidden">
                            <ShowreelLauncher />
                        </div>

                        <div className="mt-8 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                            Version 1.0 · Enterprise Offline Edition
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
